import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { dataHoraLocalMinuto } from '../utils/dataLocal'; // Onda 138: data local, não UTC
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX,
  FiUserPlus, FiCalendar, FiAlertCircle, FiCheck,
  FiBell, FiCheckCircle
} from 'react-icons/fi';
import './Visitors.css';
// Onda 108b: a tela vale nas duas verticais; o descritor diz em qual estamos.
import { useAuth } from '../context/AuthContext';
import { descritorDaVertical } from '../data/marca';

const ACCESS_TYPES = { unico: 'Dia Único', periodo: 'Período', recorrente: 'Recorrente' };
const STATUS_COLORS = { ativo: 'success', expirado: 'secondary', cancelado: 'danger' };
const STATUS_LABELS = { ativo: 'Ativo', expirado: 'Expirado', cancelado: 'Cancelado' };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}
function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR');
}
function isExpiringSoon(validUntil) {
  const diff = new Date(validUntil) - new Date();
  return diff > 0 && diff < 24 * 60 * 60 * 1000;
}

export default function Visitors() {
  const { vertical } = useAuth();
  const [tab, setTab]               = useState('visitors'); // 'visitors' | 'denied'
  const [visitors, setVisitors]     = useState([]);
  const [denied, setDenied]         = useState([]);
  const [residents, setResidents]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('ativo');
  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [unackCount, setUnackCount] = useState(0);

  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)       params.search = search;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/api/visitors', { params });
      setVisitors(res.data.data || []);
    } catch (e) {
      setError('Erro ao carregar visitantes');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus]);

  const fetchDenied = useCallback(async () => {
    try {
      const res = await api.get('/api/visitors/denied-log?limit=100');
      setDenied(res.data.data || []);
      setUnackCount((res.data.data || []).filter(d => !d.operator_ack).length);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchVisitors(); }, [fetchVisitors]);
  useEffect(() => { fetchDenied(); }, [fetchDenied]);

  useEffect(() => {
    api.get('/api/residents?status=ativo')
      .then(r => setResidents(r.data.data || []))
      .catch(() => {});
  }, []);

  const openModal = (data = {}) => {
    const now = new Date();
    const today = dataHoraLocalMinuto(now);
    const tomorrow = dataHoraLocalMinuto(new Date(now.getTime() + 24*60*60*1000));
    setForm(data.id ? { ...data } : { valid_from: today, valid_until: tomorrow, access_type: 'unico' });
    setModal(data);
    setError('');
  };

  const closeModal = () => { setModal(null); setForm({}); setError(''); };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (modal?.id) {
        await api.put(`/api/visitors/${modal.id}`, form);
        setSuccess('Visitante atualizado com sucesso');
      } else {
        await api.post('/api/visitors', form);
        setSuccess('Visitante cadastrado com sucesso');
      }
      fetchVisitors();
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar esta autorização de visitante?')) return;
    try {
      await api.delete(`/api/visitors/${id}`);
      setSuccess('Autorização cancelada');
      fetchVisitors();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao cancelar');
    }
  };

  const handleAck = async (id) => {
    try {
      await api.post(`/api/visitors/denied-log/${id}/ack`);
      fetchDenied();
    } catch (e) {}
  };

  return (
    <div className="visitors-page">
      <div className="page-header">
        <div>
          <h1>👤 Visitantes</h1>
          <p>Controle de autorizações de acesso para visitantes — Vertical {descritorDaVertical(vertical) || 'Condomínio'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> Nova Autorização
        </button>
      </div>

      {success && <div className="alert alert-success"><FiCheck /> {success}</div>}
      {error && !modal && <div className="alert alert-danger"><FiAlertCircle /> {error}</div>}

      {/* Abas */}
      <div className="tabs">
        <button className={`tab ${tab === 'visitors' ? 'active' : ''}`} onClick={() => setTab('visitors')}>
          <FiUserPlus /> Autorizações
        </button>
        <button className={`tab ${tab === 'denied' ? 'active' : ''}`} onClick={() => { setTab('denied'); fetchDenied(); }}>
          <FiBell /> Acessos Negados
          {unackCount > 0 && <span className="badge-count">{unackCount}</span>}
        </button>
      </div>

      {tab === 'visitors' && (
        <>
          {/* Filtros */}
          <div className="filters-bar">
            <div className="search-box">
              <FiSearch />
              <input
                placeholder="Buscar por nome, placa, morador..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button onClick={() => setSearch('')}><FiX /></button>}
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="expirado">Expirado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          {loading ? (
            <div className="loading-state">Carregando visitantes...</div>
          ) : visitors.length === 0 ? (
            <div className="empty-state">
              <FiUserPlus size={48} />
              <h3>Nenhuma autorização encontrada</h3>
              <p>Clique em "Nova Autorização" para cadastrar um visitante.</p>
            </div>
          ) : (
            <div className="visitors-table-wrapper">
              <table className="visitors-table">
                <thead>
                  <tr>
                    <th>Visitante</th>
                    <th>Placa</th>
                    <th>Morador / Unidade</th>
                    <th>Tipo</th>
                    <th>Validade</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map(v => (
                    <tr key={v.id} className={v.status !== 'ativo' ? 'row-inactive' : ''}>
                      <td>
                        <div className="visitor-name">{v.name}</div>
                        {v.purpose && <div className="visitor-purpose">{v.purpose}</div>}
                      </td>
                      <td>
                        <span className="plate-mono">{v.plate}</span>
                      </td>
                      <td>
                        <div>{v.resident_name}</div>
                        <div className="unit-label">{v.block ? `${v.block} — ` : ''}{v.unit}</div>
                      </td>
                      <td>
                        <span className="access-type-badge">{ACCESS_TYPES[v.access_type] || v.access_type}</span>
                      </td>
                      <td>
                        <div className="validity-dates">
                          <span><FiCalendar size={11} /> {formatDate(v.valid_from)}</span>
                          <span>até {formatDate(v.valid_until)}</span>
                        </div>
                        {v.status === 'ativo' && isExpiringSoon(v.valid_until) && (
                          <span className="expiring-soon">⚠ Expira hoje</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${STATUS_COLORS[v.status]}`}>
                          {STATUS_LABELS[v.status]}
                        </span>
                      </td>
                      <td>
                        <div className="row-actions">
                          {v.status === 'ativo' && (
                            <>
                              <button className="btn btn-icon" title="Editar" onClick={() => openModal(v)}>
                                <FiEdit2 />
                              </button>
                              <button className="btn btn-icon btn-danger" title="Cancelar" onClick={() => handleCancel(v.id)}>
                                <FiTrash2 />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'denied' && (
        <div className="denied-log">
          <p className="denied-info">
            Registros de tentativas de acesso de placas não cadastradas como moradores ou visitantes autorizados.
          </p>
          {denied.length === 0 ? (
            <div className="empty-state">
              <FiCheckCircle size={48} />
              <h3>Nenhum acesso negado registrado</h3>
            </div>
          ) : (
            <div className="denied-table-wrapper">
              <table className="visitors-table">
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Câmera</th>
                    <th>Data/Hora</th>
                    <th>Motivo</th>
                    <th>Confirmado</th>
                  </tr>
                </thead>
                <tbody>
                  {denied.map(d => (
                    <tr key={d.id} className={d.operator_ack ? '' : 'row-unack'}>
                      <td><span className="plate-mono">{d.plate}</span></td>
                      <td>{d.camera_name || `Câmera ${d.camera_id}`}</td>
                      <td>{formatDateTime(d.detected_at)}</td>
                      <td>
                        <span className="denied-reason">
                          {d.reason === 'nao_cadastrado' ? 'Não cadastrado' :
                           d.reason === 'visitante_expirado' ? 'Autorização expirada' :
                           d.reason === 'morador_inativo' ? 'Morador inativo' :
                           d.reason === 'veiculo_bloqueado' ? 'Veículo bloqueado' : d.reason}
                        </span>
                      </td>
                      <td>
                        {d.operator_ack ? (
                          <span className="ack-label"><FiCheckCircle /> Confirmado</span>
                        ) : (
                          <button className="btn btn-sm btn-primary" onClick={() => handleAck(d.id)}>
                            <FiCheck /> Confirmar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de cadastro/edição */}
      {modal !== null && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal?.id ? 'Editar Autorização' : 'Nova Autorização de Visitante'}</h2>
              <button onClick={closeModal}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger"><FiAlertCircle /> {error}</div>}
              <div className="form-grid">
                <div className="form-group full">
                  <label>Nome do Visitante *</label>
                  <input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nome completo" />
                </div>
                <div className="form-group">
                  <label>Placa do Veículo *</label>
                  <input
                    value={form.plate || ''}
                    onChange={e => setForm({...form, plate: e.target.value.toUpperCase()})}
                    placeholder="ABC1D23"
                    maxLength={8}
                    style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '0.1em' }}
                    disabled={!!modal?.id}
                  />
                </div>
                <div className="form-group">
                  <label>Telefone</label>
                  <input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(00) 00000-0000" />
                </div>
                <div className="form-group full">
                  <label>Morador Autorizador *</label>
                  <select value={form.resident_id || ''} onChange={e => setForm({...form, resident_id: e.target.value})}>
                    <option value="">Selecione o morador...</option>
                    {residents.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name} — {r.block ? `${r.block} / ` : ''}{r.unit}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo de Acesso</label>
                  <select value={form.access_type || 'unico'} onChange={e => setForm({...form, access_type: e.target.value})}>
                    <option value="unico">Dia Único</option>
                    <option value="periodo">Período (múltiplos dias)</option>
                    <option value="recorrente">Recorrente (prestador de serviço)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Autorizado a partir de *</label>
                  <input type="datetime-local" value={form.valid_from || ''} onChange={e => setForm({...form, valid_from: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Autorizado até *</label>
                  <input type="datetime-local" value={form.valid_until || ''} onChange={e => setForm({...form, valid_until: e.target.value})} />
                </div>
                <div className="form-group full">
                  <label>Motivo da Visita</label>
                  <input value={form.purpose || ''} onChange={e => setForm({...form, purpose: e.target.value})} placeholder="Ex: Visita familiar, Manutenção, Entrega..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar Autorização'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
